import React from "react";
import { useAPI } from "lib/API";
import { deletBusinessUnits, getBusinessUnit } from "requests/organization";
import { renderToast, toast } from "components/UI/ToastNotification";
import { ORGANIZATION_CONSTANTS } from "constants/organization";
import { GENERAL_CONSTANTS } from "constants/general";
import { useModalState } from "hooks/useAddonModalState";
import { navigate } from "hooks/useHookRouterCompat";
import { BusinessUnit } from "data/BusinessUnit";
import { useBusinessUnit } from "context/businessUnit";

interface UseBusinessUnitActionsProps {
    reload?: boolean;
    setReload?: (reload: boolean) => void;
}


export const useBusinessUnitActions = ({ reload, setReload }: UseBusinessUnitActionsProps) => {
    const { refetchBusinessUnits } = useBusinessUnit();
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const { modalState, showModal, hideModal } = useModalState(["delete"]);
    const [toDeleteIds, setToDeleteIds] = React.useState<number[]>([]);
    const [businessUnit, setBusinessUnit] = React.useState<BusinessUnit>();

    const deleteBusinessUnitReq = useAPI({
        deferFn: deletBusinessUnits,
        onResolve: async (result) => {
            const { deactivated = [], deleted = [], blocked = [] } = result?.data || {};

            if (blocked.length > 0) {
                // Only the unit is named. A unit can be held back by dozens of
                // products at once, and listing them made the toast unreadable
                // without telling the user anything the message does not: the
                // fix is the same for every one of them. Distinct reasons are
                // still listed, since a unit can be held back by more than one.
                const detail = blocked
                    .map((unit: any) => {
                        const reasons = (unit.reasons || [])
                            .map((entry: any) => entry.message)
                            .filter((text: string, index: number, all: string[]) => all.indexOf(text) === index)
                            .join('\n');
                        return `${unit.name} — ${reasons}`;
                    })
                    .join('\n');

                renderToast(toast.TYPE.ERROR, `${detail}`);
            } else if (deactivated.length > 0) {
                renderToast(toast.TYPE.SUCCESS, ORGANIZATION_CONSTANTS.BUSINESS_UNIT_DEACTIVATED);
            } else {
                renderToast(toast.TYPE.SUCCESS, ORGANIZATION_CONSTANTS.BUSINESS_UNIT_DELETED);
            }
            setReload?.(!reload);
            setToDeleteIds([]);
            setSelectedRows([]);
            hideModal("delete");
            // Refetch business units to update the global list
            await refetchBusinessUnits();
        },
        onReject: (err) => {
            renderToast(toast.TYPE.ERROR, err.message);
            hideModal("delete");
        },
    });

    const businessUnitDetailReq = useAPI({
        deferFn: getBusinessUnit,
        onResolve: (result) => {
            setBusinessUnit(result.data);
        },
        onReject: (error) => {
            renderToast(toast.TYPE.ERROR, error.message);
            navigate('/organizations');
        }
    });


    const handleEdit = (id: number) => {
        navigate(`/organizations/organizations-details/${id}`);
    };

    const handleDelete = (id: number) => {
        setToDeleteIds([id]);
        showModal("delete");
    };

    const handleDeleteMultiple = () => {
        const ids = selectedRows.map((row) => row.id);
        setToDeleteIds(ids);
        showModal("delete");
    };

    const onDelete = () => {
        deleteBusinessUnitReq.run(toDeleteIds);
    };

    const modalConfigs = {
        delete: {
            title: `${GENERAL_CONSTANTS.REMOVE} ${ORGANIZATION_CONSTANTS.BUSINESS_UNIT}`,
            message: `${ORGANIZATION_CONSTANTS.DELETE_UNIT_TEXT_1} ${toDeleteIds?.length > 1 ? GENERAL_CONSTANTS.THESE : GENERAL_CONSTANTS.THIS
                } ${toDeleteIds?.length > 1
                    ? ORGANIZATION_CONSTANTS.BUSINESS_UNITS
                    : ORGANIZATION_CONSTANTS.BUSINESS_UNIT
                }? ${ORGANIZATION_CONSTANTS.DELETE_UNIT_TEXT_2}`,
            onConfirm: onDelete,
            cancelText: GENERAL_CONSTANTS.BACK,
        },
    };

    return {
        selectedRows,
        setSelectedRows,
        loading,
        setLoading,
        handleEdit,
        handleDelete,
        handleDeleteMultiple,
        modalConfigs,
        modalState,
        hideModal,
        deleteBusinessUnitReq,
        businessUnitDetailReq,
        businessUnit,
    };
};
